import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useActiveTab,
  useComments,
  useCurrentStageStatus,
  useFormData,
  useRequestDetails,
  useSubFormApproval,
  useProductDetailsApproval,
  useManufacturerSiteApproval,
} from "../../../../hooks";
import { FormSectionsLayout } from "../../../common/FormApproveComponents/FormSections/components";
import Tab from "apollo-react/components/Tab";
import Tabs from "apollo-react/components/Tabs";
import StarSolid from "apollo-react-icons/StarSolid";
import ClarificationWaves from "../../../common/ClarificationWaves";
import "../../../Admin/Approvals/style.css";
import axiosInstance from "../../../../axiosInstance";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Button from "../../../common/Button";
import DrugPricingFormView from "../New_Requests/DrugPricingForm/DrugPricingFormView";
import { treeData } from "../../../common/ECTD/constants";
import Ectd from "../../../common/ECTD";
import AdditionalDocuments from "../../../common/AdditionalDocuments/AdditionalDocuments";
import AuditTrails from "../../../common/AuditTrails";

const MyRequestDetails = ({ persona }) => {
  const { t } = useTranslation();
  const { id: requestProgressId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const getFormDataRef = useRef(null);

  const {
    filledForm,
    subForms,
    loading,
    requestId,
    roleName,
    requestProgress,
    parentStage
  } = useFormData(requestProgressId, persona);

  const [comments, setComments] = useState([]);

  const { data: currentStageStatus } = useCurrentStageStatus(
    requestId,
    persona
  );

  const requestDetails = useRequestDetails(requestId, persona) || {};

  const { prevComments, handleAddComment } = useComments({
    persona,
    requestId,
    skipInitialFetch: false,
  });

  // State to hold product details and manufacturer site comments
  const [productDetailsComments, setProductDetailsComments] = useState([]);
  const [manufacturerSiteComments, setManufacturerSiteComments] = useState([]);
  const [additionalDocsComments, setAdditionalDocsComments] = useState([]);

  // fetch product details approval status
  const {
    isApproved: isProductDetailsApproved,
  } = useProductDetailsApproval({
    productDetailsId: requestDetails.productDetails?.id,
    requestId: requestId,
    persona: persona
  });

  // fetch manufacturer site approval status
  const {
    isApproved: isManufacturerSiteApproved,
  } = useManufacturerSiteApproval({
    manufacturerSiteId: requestDetails.manufacturersSite?.id,
    requestId: requestId,
    persona: persona
  });

  // Fetch product details comments
  useEffect(() => {
    const fetchProductDetailsComments = async () => {
      if (!requestDetails.productDetails?.id) {
        setProductDetailsComments([]);
        return;
      }
      try {
        const endpoint = persona === "admin"
          ? `/api/product-details-comments/${requestDetails.productDetails.id}`
          : `/api/product-details-comments/enduser/${requestDetails.productDetails.id}`;
        const response = await axiosInstance.get(endpoint);
        setProductDetailsComments(response.data || []);
      } catch (error) {
        console.error('Error fetching product details comments:', error);
        setProductDetailsComments([]);
      }
    };

    fetchProductDetailsComments();
  }, [requestDetails.productDetails?.id, persona]);

  // Fetch manufacturer site comments
  useEffect(() => {
    const fetchManufacturerSiteComments = async () => {
      if (!requestDetails.manufacturersSite?.id) {
        setManufacturerSiteComments([]);
        return;
      }
      try {
        const endpoint = persona === "admin"
          ? `/api/manufacturers-site-comments/${requestDetails.manufacturersSite.id}`
          : `/api/manufacturers-site-comments/enduser/${requestDetails.manufacturersSite.id}`;
        const response = await axiosInstance.get(endpoint);
        setManufacturerSiteComments(response.data || []);
      } catch (error) {
        console.error('Error fetching manufacturer site comments:', error);
        setManufacturerSiteComments([]);
      }
    };

    fetchManufacturerSiteComments();
  }, [requestDetails.manufacturersSite?.id, persona]);

  // count comments for product details and manufacturer site
  const productDetailsCommentsCount = productDetailsComments.length;
  const manufacturerSiteCommentsCount = manufacturerSiteComments.length;

  // use the hook for regular subforms approval
  const {
    subForms: subFormsWithApproval,
  } = useSubFormApproval({
    enduserFilledFormId: filledForm?.id,
    subForms: subForms,
    persona: persona
  });

  // separate memoization for custom forms to reduce recalculation
  const customSubForms = useMemo(() => {
    const customForms = [];
  // always show productdetails tab if the request has product_details_id or productDetails data
    if (requestDetails.productDetails || requestDetails.product_details_id) {
      customForms.push({
        id: 'custom_product_details',
        name: 'Product Details',
        isCustom: true,
        approved: isProductDetailsApproved ? 1 : 0,
        hasComments: productDetailsCommentsCount > 0,
      });
    }
    // always show manufacturerssite tab if the request has manufacturers_site_id or manufacturersSite data
    if (requestDetails.manufacturersSite || requestDetails.manufacturers_site_id) {
      customForms.push({
        id: 'custom_manufacturers_site',
        name: 'Manufacturers Site',
        isCustom: true,
        approved: isManufacturerSiteApproved ? 1 : 0,
        hasComments: manufacturerSiteCommentsCount > 0,
      });
    }
    return customForms;
  }, [
    requestDetails.productDetails,
    requestDetails.manufacturersSite,
    requestDetails.product_details_id,
    requestDetails.manufacturers_site_id,
    isProductDetailsApproved,
    isManufacturerSiteApproved,
    productDetailsCommentsCount,
    manufacturerSiteCommentsCount
  ]);

  // combine regular and custom subforms - separate dependency to avoid cascading recalculations
  const allSubForms = useMemo(() => {
    return [...subFormsWithApproval, ...customSubForms];
  }, [subFormsWithApproval, customSubForms]);

  const [activeSubFormTab, setActiveSubFormTab] = useActiveTab(allSubForms);

  const isEditable = currentStageStatus?.status === "sendback";

  const TABS = useMemo(() => [
    { label: `${parentStage?.Stage?.Form?.name}`, disabled: false, value: 0 },
    { label: "Core Assessment", disabled: false, value: 1 },
    { label: "Pricing", disabled: false, value: 2 },
    { label: "Additional Documents", disabled: false, value: 3 },
    { label: t('clarificationWaves.tabLabel'), disabled: false, value: 4 },
    { label: "Audit Trail", disabled: false, value: 5 },
  ], [parentStage, t]);

  const [activeMainTab, setActiveMainTab] = useActiveTab(
    TABS, 
    location.state?.openTab ?? 0
  );

  // Handle query parameters for tab selection
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'clarifications') {
      const clarificationsTabIndex = TABS.findIndex(tab => tab.label === t('clarificationWaves.tabLabel'));
      if (clarificationsTabIndex !== -1) {
        setActiveMainTab(clarificationsTabIndex);
      }
    }
  }, [searchParams, setActiveMainTab, TABS, t]);

  // memoize tab change handler to prevent recreation
  const handleTabChange = useCallback((event, value) => {
    setActiveMainTab(value);
  }, [setActiveMainTab]);

  // memoize form data change handler to prevent recreation
  const handleFormDataChange = useCallback((getDataFn) => {
    getFormDataRef.current = getDataFn;
  }, []);

  // Handle form resubmission
  const handleResubmitForm = async () => {
    try {
      setIsSubmitting(true);

      // Get all current form data including cached data from all tabs
      let allCurrentFormData = {};

      // Get cached form data from all tabs
      if (
        getFormDataRef.current &&
        typeof getFormDataRef.current === "function"
      ) {
        allCurrentFormData = getFormDataRef.current() || {};
      }

      // Get original data for any subforms not in cache
      try {
        const originalData = filledForm?.value
          ? JSON.parse(filledForm.value)
          : {};

        subForms.forEach((subForm) => {
          // If we don't have cached data for this form, use original data
          if (!allCurrentFormData[subForm.id] && originalData[subForm.id]) {
            allCurrentFormData[subForm.id] = originalData[subForm.id];
          }
        });
      } catch (e) {
        console.error("Error getting original form data:", e);
      }

      // Initialize with existing form data
      let parsedExistingData = {};
      try {
        parsedExistingData = filledForm?.value
          ? JSON.parse(filledForm.value)
          : {};
      } catch (e) {
        console.error("Error parsing existing form data:", e);
      }

      // Merge existing data with current form data
      const allFormData = {
        ...parsedExistingData,
        ...allCurrentFormData,
      };

      // Validate all required fields
      let allValid = true;
      const missingFields = [];

      for (const subForm of subForms) {
        const formData = allFormData[subForm.id] || {};
        let formFields = [];

        try {
          formFields = JSON.parse(subForm.json_object);
        } catch (e) {
          console.error("Error parsing subform json:", e);
          continue;
        }

        if (Array.isArray(formFields)) {
          for (const field of formFields) {
            if (
              field &&
              field.required &&
              (!formData[field.name] || formData[field.name] === "")
            ) {
              allValid = false;
              missingFields.push(
                `${field.label || field.name} in ${subForm.name}`
              );
            }
          }
        }
      }

      if (!allValid) {
        toast.error(
          `Please fill all required fields: ${missingFields.join(", ")}`,
          {
            position: "top-center",
          }
        );
        setIsSubmitting(false);
        return;
      }

      // Create a new form submission (resubmission)
      await axiosInstance.post("/api/endUserFilledForms", {
        stage_id: requestProgress?.stage_id,
        value: JSON.stringify(allFormData),
        wf_version_id: requestProgress?.Stage?.wf_version_id,
        request_id: requestId,
        request_progress_id: requestProgressId,
        is_resubmission: true,
      });

      toast.success("Form resubmitted successfully!", {
        position: "top-center",
      });

      // Navigate back to my requests list
      navigate("/customer/requests/my-requests");
    } catch (error) {
      console.error("Error resubmitting form:", error);
      toast.error("Failed to resubmit form. Please try again.", {
        position: "top-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Tabs
        value={activeMainTab}
        onChange={handleTabChange}
        truncate
        className="form-approve-tabs"
        shape="box"
      >
        {TABS.map((tab, index) => (
          <Tab
            key={index}
            label={
              <>
                {activeMainTab === index && (
                  <StarSolid style={{ marginRight: "8px" }} />
                )}
                {tab.label}
              </>
            }
            disabled={
              tab.disabled || roleName === "Principal Investigator (PI)"
            }
            className="form-approve-tab"
          />
        ))}
      </Tabs>

      {TABS[activeMainTab]?.value === 0 && (
        <>
          <FormSectionsLayout
            forms={allSubForms}
            activeTab={activeSubFormTab}
            onTabChange={setActiveSubFormTab}
            isLoading={loading}
            prevComments={prevComments}
            requestDetails={requestDetails}
            requestProgressId={requestProgressId}
            persona={persona}
            showRequestBadges={true}
            filledForm={filledForm}
            requestId={requestId}
            handleAddComment={handleAddComment}
            enableAddComment={isEditable}
            editable={isEditable}
            onFormDataChange={handleFormDataChange}
            requestProgress={requestProgress}
            hasProductDetails={!!requestDetails.productDetails}
            hasManufacturersSite={!!requestDetails.manufacturersSite}
            isSendbackMode={isEditable}
            roleName={persona}
            skipApprovalFetch={true}
            formsWithApprovalStatus={allSubForms}
            productDetailsData={requestDetails.productDetails}
          />

          {isEditable && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "30px",
                gap: 16,
                padding: "0 24px",
              }}
            >
              <Button
                className="actionButtonStyle"
                color="secondary"
                text="Cancel"
                style={{ width: "120px", height: "40px" }}
                onClick={() => navigate("/customer/requests/my-requests")}
                disabled={isSubmitting}
              />
              <Button
                className="actionButtonStyle"
                color="primary"
                text={isSubmitting ? "Submitting..." : "Resubmit"}
                style={{ width: "120px", height: "40px" }}
                onClick={handleResubmitForm}
                disabled={isSubmitting}
              />
            </div>
          )}
        </>
      )}
      {TABS[activeMainTab]?.value === 1 && (
        <Ectd
        role={persona}
        requestId={requestId}
        ectdFileId={requestProgress?.request?.ectd_file_id}
        isSendbackMode={isEditable}
        ectdTreeComponentProps={{
          treeData,
          comments,
          setComments,
          ectdVersion: requestDetails?.ectd_version,
        }}/>
      )}
      {TABS[activeMainTab]?.value === 2 && (
        <DrugPricingFormView 
          drugPricingFormId={requestDetails.drug_pricing_form_id}
          persona={persona}
          role={persona}
          comments={comments}
          setComments={setComments}
        />
      )}
      {TABS[activeMainTab]?.value === 3 && (
        <AdditionalDocuments 
          requestId={requestId}
          certificateFileId={requestProgress?.request?.certificate_file_id}
          role={persona}
          persona={persona}
          requestProgressStatus={requestProgress?.status}
          comments={additionalDocsComments}
          setComments={setAdditionalDocsComments}
        />
      )}
      {TABS[activeMainTab]?.value === 4 && (
        <ClarificationWaves
          subForms={subForms}
          requestProgressId={requestProgressId}
          persona={persona}
          requestId={requestId}
          filledForm={filledForm}
          requestProgress={requestProgress}
          requestDetails={requestDetails}
          roleName={roleName}
          parentStage={parentStage}
          comments={comments}
          setComments={setComments}
        />
      )}
      {TABS[activeMainTab]?.value === 5 && (
        <AuditTrails persona={persona} requestID={requestProgress?.request_id}
        role={persona} />
      )}
    </>
  );
};

export default MyRequestDetails;
